import { Injectable } from '@angular/core';

import { Meeting } from '@cds/betting-offer/tags';
import { SportConstant } from '@frontend/sports/common/core/data-access/constants';
import { Nullable } from '@frontend/sports/common/core/utils/extended-types';
import { HorseRaceSortingDirection, WinGameFixture, WinGameFixturePreview, WinGameParticipant } from '@frontend/sports/racing/feature/models';
import { NOT_APPLICABLE, TrackingService, TrackingSource, trackingConstants } from '@frontend/sports/tracking/feature';
import { IntlService } from '@frontend/vanilla/core';

import { getDayOfRace } from '../../horse-racing-shared/helpers';

@Injectable({
    providedIn: 'root',
})
export class GreyhoundTrackingService {
    constructor(
        private trackingService: TrackingService,
        private intlService: IntlService,
    ) {}

    trackViewFullCard(race: WinGameFixture, index: TrackingSource, fixtureUrl: string): void {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'Sports Lobby Widgets',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'Sports event',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'Click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: 'NextRaces',
            [trackingConstants.COMPONENT_LOCATION_EVENT]: race.meeting.name.value,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'more',
            [trackingConstants.COMPONENT_URL_CLICKED]: fixtureUrl,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: index.position,
            [trackingConstants.SPORT_EVENT_ID]: race.id,
            [trackingConstants.SPORT_ID]: race.sport.id,
            [trackingConstants.COMPONENT_MODULE_NAME]: 'Greyhound_NextRaces',
            [trackingConstants.COMPONENT_MODULE_POSITION]: this.intlService.formatDate(race.startDate, 'HH:mm') || '',
            [trackingConstants.COMPONENT_PAGE_LAYOUT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_MODULE_CUSTOM_NAME]: race.meeting.name.value,
            [trackingConstants.COMPONENT_MODULE_SOURCE]: NOT_APPLICABLE,
        });
    }

    trackExpandCollapse(
        isExpanded: boolean,
        race: WinGameFixturePreview,
        participant: Nullable<WinGameParticipant>,
        isTimeformTip: boolean = false,
    ): void {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: race.sport.name.value,
            [trackingConstants.COMPONENT_LABEL_EVENT]: isTimeformTip ? 'timeform_tip' : 'race card',
            [trackingConstants.COMPONENT_ACTION_EVENT]: isExpanded ? 'expand' : 'collapse',
            [trackingConstants.COMPONENT_POSITION_EVENT]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getFixtureMeetingandTime(race),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: participant ? participant.name.value : this.getFixtureMeetingandTime(race),
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }

    private getFixtureMeetingandTime(fixture: WinGameFixturePreview): string {
        return fixture.meeting.name.value + ' / ' + this.intlService.formatDate(fixture.startDate, 'HH:mm');
    }

    trackRaceDetailMeeting(title: string | undefined, meeting: Meeting, url: any, fixture: WinGameFixturePreview): void {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: fixture.sport.name.value,
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'sub navigation',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: 'race card',
            [trackingConstants.COMPONENT_LOCATION_EVENT]: fixture.region.name.value,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: meeting.name.value + '/' + title,
            [trackingConstants.COMPONENT_URL_CLICKED]: url,
        });
    }

    trackSorting(race: WinGameFixturePreview, sortBy?: HorseRaceSortingDirection, positionEvent: string = NOT_APPLICABLE) {
        const sorting =
            sortBy === undefined ? 'sort preference' : sortBy === 'odds' ? 'odds sort' : sortBy === 'card-number' ? 'race card-sort' : 'AtoZ';
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: race.sport.name.value,
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'race card',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: positionEvent !== NOT_APPLICABLE ? positionEvent : race.meeting.name.value,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getFixtureMeetingandTime(race),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: sorting,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }

    trackselectMarket(race: WinGameFixturePreview, selectedMarket: string, url: string = NOT_APPLICABLE) {
        const market = selectedMarket !== 'Win' ? selectedMarket : 'race winner';
        if (selectedMarket === 'Win Each Way - Extra Place') {
            this.trackingDerivedMarkets(race.fixtureId, 'market', market!);
        } else {
            this.trackingService.track(trackingConstants.EVENT_TRACKING, {
                [trackingConstants.COMPONENT_CATEGORY_EVENT]: race.sport.name.value,
                [trackingConstants.COMPONENT_LABEL_EVENT]: 'sub navigation',
                [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
                [trackingConstants.COMPONENT_POSITION_EVENT]: 'race card',
                [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getFixtureMeetingandTime(race),
                [trackingConstants.COMPONENT_EVENT_DETAILS]: race.sport.id === SportConstant.Horses ? selectedMarket : market,
                [trackingConstants.COMPONENT_URL_CLICKED]: url,
            });
        }
    }

    trackingDerivedMarkets(fixtureId: string, eventdetail: string, position: string) {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'market switcher',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'markets',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'select',
            [trackingConstants.COMPONENT_POSITION_EVENT]: position,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: 'horse racing',
            [trackingConstants.COMPONENT_EVENT_DETAILS]: eventdetail,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
            [trackingConstants.MARKET_ID]: fixtureId,
        });
    }

    trackRaceModule(fixture: WinGameFixturePreview, url: string, position: number) {
        const day = getDayOfRace(fixture.startDate);
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'sports lobby widgets',
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'sports event',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: day,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: fixture.meeting.name.value,
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'more',
            [trackingConstants.COMPONENT_URL_CLICKED]: url,
            [trackingConstants.COMPONENT_CONTENT_POSITION]: position,
            [trackingConstants.COMPONENT_MODULE_POSITION]: this.intlService.formatDate(fixture.startDate, 'HH:mm') || '',
            [trackingConstants.COMPONENT_SPORT_ID]: fixture.sport.id,
            [trackingConstants.Event_ID]: fixture.id,
            [trackingConstants.COMPONENT_MODULE_NAME]: 'Greyhound_Racecard',
            [trackingConstants.COMPONENT_MODULE_CUSTOM_NAME]: fixture.meeting.name.value,
            [trackingConstants.COMPONENT_MODULE_SOURCE]: NOT_APPLICABLE,
            [trackingConstants.COMPONENT_PAGE_LAYOUT]: NOT_APPLICABLE,
        });
    }

    trackSubMarket(selectedSubMarketTitle: string | undefined, selectedMarketTitle: string | undefined, fixture: WinGameFixturePreview): void {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: fixture.sport.name.value,
            [trackingConstants.COMPONENT_LABEL_EVENT]: 'filter',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: 'race card_' + selectedMarketTitle,
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getFixtureMeetingandTime(fixture),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: selectedSubMarketTitle,
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }

    getAdditionalTrackingOptions(
        fixture: WinGameFixturePreview,
        trackingSource: string,
        isTimeform: boolean = false,
        isBestOddsEligible?: boolean,
    ): { [key: string]: string } {
        return {
            [trackingConstants.COMPONENT_MODULE_CUSTOM_NAME]: fixture.meeting.name.value,
            [trackingConstants.COMPONENT_MODULE_NAME]: isTimeform ? 'horseracing_timeform_tip' : trackingSource.split('/')[0],
            [trackingConstants.COMPONENT_MODULE_POSITION]: this.intlService.formatDate(fixture.startDate, 'HH:mm'),
            [trackingConstants.COMPONENT_MODULE_SOURCE]: '',
            [trackingConstants.COMPONENT_BET_SOURCE]: trackingSource,
            [trackingConstants.BET_CALENDER]: getDayOfRace(fixture.startDate),
            [trackingConstants.BET_DETAILS]: isBestOddsEligible ? 'BOG' : NOT_APPLICABLE,
        };
    }

    trackClear(fixture: WinGameFixturePreview, isForecast: boolean) {
        this.trackingService.track(trackingConstants.EVENT_TRACKING, {
            [trackingConstants.COMPONENT_CATEGORY_EVENT]: 'horse racing',
            [trackingConstants.COMPONENT_LABEL_EVENT]: isForecast ? 'forecast' : 'tricast',
            [trackingConstants.COMPONENT_ACTION_EVENT]: 'click',
            [trackingConstants.COMPONENT_POSITION_EVENT]: 'race card',
            [trackingConstants.COMPONENT_LOCATION_EVENT]: this.getFixtureMeetingandTime(fixture),
            [trackingConstants.COMPONENT_EVENT_DETAILS]: 'clear',
            [trackingConstants.COMPONENT_URL_CLICKED]: NOT_APPLICABLE,
        });
    }
}
